import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, UserPlus, Copy, Search, Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { AuditTab } from "@/pages/admin/Sales";

const inp = "w-full h-11 rounded-xl border border-[#EADEC9] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1DB99A]";
export const PLAN_OPTS = ["IDEAS", "BUSINESS_BUILDER", "COACHING"];
export const ASSIGN_OPTS = [["FREE", "Free (1 free idea)"], ...PLAN_OPTS.map((p) => [p, p.replace("_", " ")])];

export function InviteLink({ token, onClose, kind = "invite" }) {
  const link = token ? `${window.location.origin}/setup-password?token=${token}` : "";
  return (
    <Dialog open={!!token} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="invite-link-dialog">
        <DialogHeader><DialogTitle>{kind === "invite" ? "Invite link created" : "Password reset link"}</DialogTitle>
          <DialogDescription>Send this secure link to the user. They'll set their own password. Valid for {kind === "invite" ? "72" : "48"} hours.</DialogDescription></DialogHeader>
        <div className="rounded-xl bg-[#F5F0E6] p-3 text-xs break-all" data-testid="invite-link-value">{link}</div>
        <button className="sb-btn-primary w-full" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="invite-link-copy-btn"><Copy className="h-4 w-4" />Copy link</button>
      </DialogContent>
    </Dialog>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader />;
  const cards = [["Total users", s.total_users], ["Ideas users", s.plans.IDEAS], ["Builder users", s.plans.BUSINESS_BUILDER], ["Coaching users", s.plans.COACHING],
    ["Active (7d)", s.active_users], ["New (7d)", s.new_users], ["Businesses", s.businesses], ["Avg. progress", `${s.avg_progress}%`]];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(([l, v]) => <div key={l} className="sb-card p-4" data-testid={`admin-stat-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}><div className="text-xs text-[#8C9BA5] font-semibold">{l}</div><div className="font-display text-2xl font-extrabold mt-1">{v}</div></div>)}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="sb-card p-5"><div className="sb-eyebrow mb-3">Users by current stage</div>
          {["IDEA", "VALIDATE", "BUILD", "REGISTER", "SET UP", "LAUNCH", "GROW"].map((k) => {
            const n = s.users_by_stage[k] || 0;
            return <div key={k} className="flex items-center gap-3 py-1.5 text-sm"><span className="w-20 font-bold text-xs">{k}</span><div className="flex-1 h-2 bg-[#EADEC9] rounded-full overflow-hidden"><div className="h-full bg-[#1DB99A]" style={{ width: `${s.businesses ? (100 * n) / s.businesses : 0}%` }} /></div><span className="w-6 text-right text-xs">{n}</span></div>;
          })}
        </div>
        <div className="sb-card p-5"><div className="sb-eyebrow mb-3">Content</div>
          <div className="grid grid-cols-3 gap-3 text-center">{[["Ideas", s.ideas], ["Lessons", s.lessons], ["Screens", s.screens]].map(([l, v]) => <div key={l} className="rounded-xl bg-[#FBF8F2] p-3"><div className="font-display text-xl font-extrabold">{v}</div><div className="text-xs text-[#8C9BA5]">{l}</div></div>)}</div>
          <div className="text-sm text-[#5C6B73] mt-4">Coach booking requests: <b>{s.bookings}</b> · Suspended: <b>{s.suspended}</b></div>
        </div>
      </div>
    </div>
  );
}

function Users() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const blank = { first_name: "", last_name: "", email: "", phone: "", plan: "BUSINESS_BUILDER", status: "active", start_date: new Date().toISOString().slice(0, 10), expiry_date: "", notes: "" };
  const [f, setF] = useState(blank);
  const load = () => api.get("/admin/users", { params: { q } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", f);
      setOpen(false); setF(blank); setToken(data.token); load();
      toast.success("User created");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C9BA5]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or WhatsApp" className={inp + " pl-9"} data-testid="admin-users-search" /></form>
        <button onClick={() => setOpen(true)} className="sb-btn-primary min-h-[44px] text-sm" data-testid="admin-create-user-btn"><UserPlus className="h-4 w-4" />Create user</button>
      </div>
      {!list ? <Loader /> : (
        <div className="sb-card divide-y divide-[#EADEC9]" data-testid="admin-users-list">
          {list.map((u) => (
            <Link key={u.id} to={`/admin/users/${u.id}`} className="flex items-center gap-3 p-4 hover:bg-[#FBF8F2]" data-testid={`admin-user-row-${u.email}`}>
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{u.first_name} {u.last_name} {u.role === "admin" && <span className="sb-chip bg-[#1E2B3C] text-white ml-1">admin</span>}</div><div className="text-xs text-[#8C9BA5] truncate">{u.email}</div></div>
              <span className="sb-chip bg-[#E8F8F4] text-[#179B80] hidden sm:inline-flex">{u.plan}</span>
              <span className={`sb-chip ${u.status === "active" ? "bg-emerald-50 text-emerald-700" : u.status === "suspended" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{u.status}</span>
            </Link>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-create-user-dialog">
          <DialogHeader><DialogTitle>Create user</DialogTitle><DialogDescription>An invite link is generated so the user sets their own password.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="grid grid-cols-2 gap-3">
            {[["first_name", "First name", true], ["last_name", "Last name"], ["email", "Email", true], ["phone", "Phone"]].map(([k, l, req]) => (
              <label key={k} className={k === "email" ? "col-span-2" : ""}><span className="text-xs font-semibold text-[#5C6B73]">{l}</span><input required={req} type={k === "email" ? "email" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp + " mt-1"} data-testid={`admin-new-${k}`} /></label>
            ))}
            <label><span className="text-xs font-semibold text-[#5C6B73]">Access level</span><select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} className={inp + " mt-1"} data-testid="admin-new-plan">{ASSIGN_OPTS.map(([p, l]) => <option key={p} value={p}>{l}</option>)}</select></label>
            <label><span className="text-xs font-semibold text-[#5C6B73]">Status</span><select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inp + " mt-1"} data-testid="admin-new-status"><option value="active">active</option><option value="suspended">suspended</option></select></label>
            <label><span className="text-xs font-semibold text-[#5C6B73]">Start date</span><input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inp + " mt-1"} data-testid="admin-new-start" /></label>
            <label><span className="text-xs font-semibold text-[#5C6B73]">Expiry date</span><input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className={inp + " mt-1"} data-testid="admin-new-expiry" /></label>
            <label className="col-span-2"><span className="text-xs font-semibold text-[#5C6B73]">Notes</span><textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="w-full rounded-xl border border-[#EADEC9] p-3 text-sm mt-1" data-testid="admin-new-notes" /></label>
            <button className="sb-btn-primary col-span-2" data-testid="admin-new-submit-btn">CREATE USER</button>
          </form>
        </DialogContent>
      </Dialog>
      <InviteLink token={token} onClose={() => setToken(null)} />
    </div>
  );
}

const COLLS = [["business_ideas", "Business ideas", "number", "name"], ["lessons", "Courses / lessons", "lesson_id", "title_en"], ["lesson_screens", "Lesson screens", "id", "title_en"],
  ["exercises", "Exercises", "lesson_id", "save_destination"], ["checklist_items", "Checklists", "key", "label_en"], ["software_tools", "Tools", "id", "name"],
  ["coaches", "Coaches", "id", "name"], ["templates", "Templates", "id", "title"], ["upsell_products", "Upsell products", "key", "name"], ["official_sources", "Official sources", "key", "name"]];
const SKIP = ["id", "created_at", "updated_at", "edited_by"];

function RecordEditor({ rec, coll, keyField, onClose, onSaved }) {
  const [d, setD] = useState({});
  useEffect(() => {
    if (!rec) return;
    const o = {};
    Object.entries(rec).forEach(([k, v]) => { if (!SKIP.includes(k)) o[k] = typeof v === "object" && v !== null ? JSON.stringify(v, null, 2) : v; });
    if (!("archived" in o)) o.archived = false;
    setD(o);
  }, [rec]);
  if (!rec) return null;
  const isNew = !rec[keyField];
  const save = async () => {
    try {
      const body = {};
      Object.entries(d).forEach(([k, v]) => {
        const orig = rec[k];
        body[k] = typeof orig === "object" && orig !== null ? JSON.parse(v) : typeof orig === "number" ? Number(v) : v;
      });
      if (isNew) await api.post(`/admin/content/${coll}`, body);
      else await api.patch(`/admin/content/${coll}/${encodeURIComponent(rec[keyField])}`, body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e instanceof SyntaxError ? "Invalid JSON in a list/object field" : errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="cms-editor-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New record" : `Edit ${rec[keyField]}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {Object.entries(d).map(([k, v]) => (
            <label key={k} className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C6B73]">{k}</span>
              {typeof v === "boolean" ? <div className="mt-1"><Switch checked={v} onCheckedChange={(x) => setD({ ...d, [k]: x })} data-testid={`cms-field-${k}`} /></div>
                : String(v ?? "").length > 70 || typeof rec[k] === "object"
                  ? <textarea value={v ?? ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} rows={Math.min(10, String(v ?? "").split("\n").length + 2)} className="mt-1 w-full rounded-xl border border-[#EADEC9] p-3 text-sm font-mono" data-testid={`cms-field-${k}`} />
                  : <input value={v ?? ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} className={inp + " mt-1"} disabled={!isNew && k === keyField} data-testid={`cms-field-${k}`} />}
            </label>
          ))}
        </div>
        <button onClick={save} className="sb-btn-primary w-full" data-testid="cms-save-btn">Save</button>
      </DialogContent>
    </Dialog>
  );
}

function Content() {
  const [coll, setColl] = useState(COLLS[0]);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [lessonId, setLessonId] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => { setRows(null); api.get(`/admin/content/${coll[0]}`, { params: { q, lesson_id: lessonId } }).then((r) => setRows(r.data)); };
  useEffect(() => { load(); }, [coll]); // eslint-disable-line
  const blank = () => { const o = {}; Object.entries(rows?.[0] || { name: "" }).forEach(([k, v]) => { o[k] = typeof v === "number" ? 0 : typeof v === "boolean" ? false : Array.isArray(v) ? [] : typeof v === "object" && v ? {} : ""; }); delete o[coll[2]]; return o; };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={coll[0]} onChange={(e) => setColl(COLLS.find((c) => c[0] === e.target.value))} className={inp + " w-auto"} data-testid="cms-collection-select">{COLLS.map((c) => <option key={c[0]} value={c[0]}>{c[1]}</option>)}</select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[200px]">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className={inp} data-testid="cms-search-input" />
          {coll[0] === "lesson_screens" && <input value={lessonId} onChange={(e) => setLessonId(e.target.value.toUpperCase())} placeholder="Lesson ID e.g. IDEA-01" className={inp + " w-40"} data-testid="cms-lesson-filter" />}
          <button className="sb-btn-ghost min-h-[44px] text-sm">Go</button>
        </form>
        <button onClick={() => setEdit(blank())} className="sb-btn-primary min-h-[44px] text-sm" data-testid="cms-add-btn"><Plus className="h-4 w-4" />Add</button>
      </div>
      {!rows ? <Loader /> : (
        <div className="sb-card divide-y divide-[#EADEC9] max-h-[65vh] overflow-y-auto" data-testid="cms-rows">
          {rows.slice(0, 300).map((r) => (
            <button key={r.id || r[coll[2]]} onClick={() => setEdit(r)} className="w-full text-left flex items-center gap-3 p-3 hover:bg-[#FBF8F2]" data-testid={`cms-row-${r[coll[2]]}`}>
              <span className="text-xs font-mono text-[#8C9BA5] w-24 shrink-0 truncate">{coll[0] === "lesson_screens" ? `${r.lesson_id}#${r.screen_no}` : r[coll[2]]}</span>
              <span className="text-sm font-medium flex-1 truncate">{r[coll[3]] || r.title || r.name}</span>
              {r.archived && <span className="sb-chip bg-rose-50 text-rose-700">archived</span>}
            </button>
          ))}
          {rows.length > 300 && <div className="p-3 text-xs text-[#8C9BA5]">Showing 300 of {rows.length} — refine your search.</div>}
        </div>
      )}
      <RecordEditor rec={edit} coll={coll[0]} keyField={coll[2]} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}

function Sync() {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/sync/logs").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sync");
      data.status === "success" ? toast.success(`Synced: ${data.counts.ideas} ideas, ${data.counts.lessons} lessons, ${data.counts.screens} screens`) : toast.error(data.error);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="sb-card p-6">
        <h3 className="font-display font-extrabold text-lg">Google Sheet content</h3>
        <p className="text-sm text-[#5C6B73] mt-1">Imports ideas, stages, lessons, EN/FR screens, audio, images and official sources from “SideBiz Curriculum Production Master”. Existing records are updated by stable IDs (Lesson ID + Screen #); user progress, answers and accounts are never touched.</p>
        <button onClick={run} disabled={busy} className="sb-btn-primary mt-4" data-testid="admin-sync-btn"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />{busy ? "Syncing…" : "SYNC GOOGLE SHEET CONTENT"}</button>
      </div>
      <div className="sb-card divide-y divide-[#EADEC9]" data-testid="sync-logs">
        {logs.map((l) => (
          <div key={l.id} className="p-4 text-sm flex flex-wrap gap-x-4 gap-y-1">
            <span className={`sb-chip ${l.status === "success" ? "bg-emerald-50 text-emerald-700" : l.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{l.status}</span>
            <span className="text-[#5C6B73]">{new Date(l.started_at).toLocaleString()} · {l.trigger}{l.by ? ` · ${l.by}` : ""}</span>
            {l.counts && <span className="text-xs text-[#8C9BA5] w-full">{Object.entries(l.counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>}
            {l.error && <span className="text-xs text-rose-700 w-full">{l.error}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Bookings() {
  const [list, setList] = useState(null);
  const load = () => api.get("/admin/bookings").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  if (!list) return <Loader />;
  const set = async (id, status) => { await api.patch(`/admin/bookings/${id}`, { status }); load(); };
  return (
    <div className="sb-card divide-y divide-[#EADEC9]" data-testid="admin-bookings">
      {!list.length && <div className="p-6 text-sm text-[#8C9BA5]">No booking requests yet.</div>}
      {list.map((b) => (
        <div key={b.id} className="p-4 flex flex-wrap items-center gap-3 text-sm">
          <div className="flex-1 min-w-[200px]"><b>{b.user_name}</b> · {b.user_email}<div className="text-xs text-[#5C6B73]">{b.session.name} with {b.coach_name} · {b.preferred_time}</div>{b.note && <div className="text-xs text-[#8C9BA5]">“{b.note}”</div>}</div>
          <select value={b.status} onChange={(e) => set(b.id, e.target.value)} className={inp + " w-auto h-9"} data-testid={`booking-status-${b.id}`}>{["requested", "confirmed", "completed", "cancelled"].map((s) => <option key={s}>{s}</option>)}</select>
        </div>
      ))}
    </div>
  );
}

function PlanSettings() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/settings/plans").then((r) => setD(r.data)); }, []);
  if (!d) return <Loader />;
  const setPlan = (k, f, v) => setD({ ...d, plans: { ...d.plans, [k]: { ...d.plans[k], [f]: v } } });
  const save = async () => {
    try { const { data } = await api.put("/admin/settings/plans", d); setD(data); toast.success("Plan settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-plan-settings">
      <section className="sb-card p-5">
        <div className="sb-eyebrow mb-2">WhatsApp upgrade number</div>
        <input value={d.whatsapp_number} onChange={(e) => setD({ ...d, whatsapp_number: e.target.value })} placeholder="e.g. 230 5xxx xxxx (country code + number)" className={inp} data-testid="admin-whatsapp-input" />
        <p className="text-xs text-[#8C9BA5] mt-2">Used by every “Request plan” button. Users' messages are prefilled with the plan and their account email.</p>
      </section>
      {PLAN_OPTS.map((k) => (
        <section key={k} className="sb-card p-5 grid sm:grid-cols-3 gap-3" data-testid={`admin-plan-${k}`}>
          <div className="sm:col-span-3 font-display font-extrabold">{k.replace("_", " ")}</div>
          <label><span className="text-xs font-semibold text-[#5C6B73]">Price</span><input value={d.plans[k]?.price || ""} onChange={(e) => setPlan(k, "price", e.target.value)} className={inp + " mt-1"} data-testid={`admin-plan-price-${k}`} /></label>
          <label className="sm:col-span-2"><span className="text-xs font-semibold text-[#5C6B73]">Why choose it (one line)</span><input value={d.plans[k]?.reason || ""} onChange={(e) => setPlan(k, "reason", e.target.value)} className={inp + " mt-1"} data-testid={`admin-plan-reason-${k}`} /></label>
        </section>
      ))}
      <button onClick={save} className="sb-btn-primary" data-testid="admin-plan-settings-save-btn">Save plan settings</button>
    </div>
  );
}

export default function Admin() {
  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="Super Admin" title="Sidebizz administration" />
      <Link to="/admin/sales" className="sb-btn-primary text-sm mb-4 inline-flex" data-testid="admin-open-sales-btn"><TrendingUp className="h-4 w-4" />Open Sales</Link>
      <Tabs defaultValue="overview">
        <TabsList className="bg-[#F5F0E6] rounded-xl flex-wrap h-auto">
          {["overview", "users", "plans", "content", "sync", "bookings", "audit"].map((k) => <TabsTrigger key={k} value={k} className="rounded-lg capitalize" data-testid={`admin-tab-${k}`}>{k}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview" className="mt-5"><Overview /></TabsContent>
        <TabsContent value="plans" className="mt-5"><PlanSettings /></TabsContent>
        <TabsContent value="audit" className="mt-5"><AuditTab /></TabsContent>
        <TabsContent value="users" className="mt-5"><Users /></TabsContent>
        <TabsContent value="content" className="mt-5"><Content /></TabsContent>
        <TabsContent value="sync" className="mt-5"><Sync /></TabsContent>
        <TabsContent value="bookings" className="mt-5"><Bookings /></TabsContent>
      </Tabs>
    </div>
  );
}
